import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../config/api';

/* ── Theme tokens ─────────────────────────────────────────────────────────── */
const DARK = {
  page:         '#0f172a',
  surface:      '#1e293b',
  surfaceDeep:  '#162032',
  surfaceInset: '#0a1628',
  border:       '#334155',
  borderSoft:   '#1e293b',
  text:         '#f1f5f9',
  textSecondary:'#94a3b8',
  textMuted:    '#64748b',
  textFaint:    '#475569',
  inputBg:      '#0f172a',
  headBg:       '#0d1826',
  rowHover:     '#162032',
  expandedBg:   '#0d1826',
  paginationBg: '#162032',
  errorBg:      '#450a0a',
  errorBorder:  '#7f1d1d',
  errorText:    '#fca5a5',
  codeBg:       '#0a1628',
  codeText:     '#4ade80',
  metaTileBg:   '#0f172a',
  flagOnBg:     '#172554',
  flagOnText:   '#93c5fd',
  flagOnBorder: '#1e40af',
  flagOffBg:    '#0f172a',
  flagOffText:  '#334155',
  flagOffBorder:'#1e293b',
};

const LIGHT = {
  page:         '#f8fafc',
  surface:      '#ffffff',
  surfaceDeep:  '#f1f5f9',
  surfaceInset: '#f8fafc',
  border:       '#e2e8f0',
  borderSoft:   '#e2e8f0',
  text:         '#0f172a',
  textSecondary:'#475569',
  textMuted:    '#64748b',
  textFaint:    '#94a3b8',
  inputBg:      '#ffffff',
  headBg:       '#f1f5f9',
  rowHover:     '#f8fafc',
  expandedBg:   '#f1f5f9',
  paginationBg: '#f8fafc',
  errorBg:      '#fef2f2',
  errorBorder:  '#fecaca',
  errorText:    '#dc2626',
  codeBg:       '#0f172a',
  codeText:     '#4ade80',
  metaTileBg:   '#f8fafc',
  flagOnBg:     '#eff6ff',
  flagOnText:   '#1d4ed8',
  flagOnBorder: '#bfdbfe',
  flagOffBg:    '#f8fafc',
  flagOffText:  '#94a3b8',
  flagOffBorder:'#e2e8f0',
};

/* ── Domain colours (work on both themes) ────────────────────────────────── */
const DOMAINS = {
  jobs:      { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6',  darkBg: '#1d4ed820', darkText: '#60a5fa' },
  invoice:   { bg: '#ede9fe', text: '#6d28d9', dot: '#8b5cf6',  darkBg: '#7c3aed20', darkText: '#c4b5fd' },
  invoicing: { bg: '#ede9fe', text: '#6d28d9', dot: '#8b5cf6',  darkBg: '#7c3aed20', darkText: '#c4b5fd' },
  payment:   { bg: '#fce7f3', text: '#be185d', dot: '#ec4899',  darkBg: '#be185d20', darkText: '#f9a8d4' },
  customer:  { bg: '#d1fae5', text: '#065f46', dot: '#10b981',  darkBg: '#06652620', darkText: '#6ee7b7' },
  customers: { bg: '#d1fae5', text: '#065f46', dot: '#10b981',  darkBg: '#06652620', darkText: '#6ee7b7' },
  route:     { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b',  darkBg: '#b4530920', darkText: '#fcd34d' },
  routes:    { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b',  darkBg: '#b4530920', darkText: '#fcd34d' },
  chemicals: { bg: '#ccfbf1', text: '#0f766e', dot: '#14b8a6',  darkBg: '#0f766e20', darkText: '#5eead4' },
  alerts:    { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444',  darkBg: '#dc262620', darkText: '#fca5a5' },
};
const getDomain = (d = '', dark) => {
  const k = DOMAINS[d.toLowerCase()];
  if (!k) return dark
    ? { bg: '#33415520', text: '#94a3b8', dot: '#64748b' }
    : { bg: '#f1f5f9',   text: '#64748b', dot: '#94a3b8' };
  return dark ? { bg: k.darkBg, text: k.darkText, dot: k.dot }
              : { bg: k.bg,     text: k.text,      dot: k.dot };
};

/* ── Formatters ──────────────────────────────────────────────────────────── */
const fmtDate = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
};
const trunc = (s, n = 85) => !s ? '—' : s.length > n ? s.slice(0, n) + '…' : s;
const msLabel = (v) => v != null && v !== '' ? `${Number(v).toFixed(0)} ms` : '—';

/* ── Reusable sub-components ─────────────────────────────────────────────── */
const SectionLabel = ({ children, t }) => (
  <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: t.textMuted }}>{children}</p>
);

const MetaTile = ({ label, value, accent, t }) => (
  <div style={{ background: t.metaTileBg, border: `1px solid ${t.border}`, borderRadius: 10,
    padding: '12px 16px', minWidth: 120 }}>
    <p style={{ margin: '0 0 5px', fontSize: 11, color: t.textMuted, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</p>
    <p style={{ margin: 0, fontSize: 17, fontWeight: 800, color: accent }}>{String(value ?? '—')}</p>
  </div>
);

const FlagPill = ({ on, label, t }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
    background: on ? t.flagOnBg  : t.flagOffBg,
    color:      on ? t.flagOnText : t.flagOffText,
    border:     `1px solid ${on ? t.flagOnBorder : t.flagOffBorder}`,
  }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
      background: on ? t.flagOnText : t.flagOffText }} />
    {label}
  </span>
);

const TagChip = ({ label, color }) => (
  <code style={{ padding: '4px 11px', borderRadius: 6, fontSize: 13,
    background: `${color}18`, color, border: `1px solid ${color}30`,
    fontFamily: 'ui-monospace, "Cascadia Code", monospace', fontWeight: 600 }}>
    {label}
  </code>
);

/* ═════════════════════════════════════════════════════════════════════════ */
const QueryHistory = ({ onBack, embedded = false, dark: darkProp }) => {
  const [darkLocal, setDarkLocal]          = useState(true);
  const dark                               = darkProp !== undefined ? darkProp : darkLocal;
  const t                                  = dark ? DARK : LIGHT;

  const [queries, setQueries]             = useState([]);
  const [pagination, setPagination]       = useState({});
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [postgresEnabled, setPostgresEnabled] = useState(true);
  const [expanded, setExpanded]           = useState(null);

  const [page, setPage]                   = useState(1);
  const [perPage]                         = useState(25);
  const [days, setDays]                   = useState(30);
  const [domain, setDomain]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [search, setSearch]               = useState('');
  const [searchInput, setSearchInput]     = useState('');

  // Feedback approve/correct state
  const [correctionId, setCorrectionId]         = useState(null);
  const [correctionSql, setCorrectionSql]       = useState('');
  const [correctionStatus, setCorrectionStatus] = useState(null);
  const [approveStatus, setApproveStatus]       = useState({});
  const [correctedQueries, setCorrectedQueries] = useState({});   // { queryId: correctedSql }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ page, per_page: perPage });
      if (days)         p.append('days', days);
      if (domain)       p.append('domain', domain);
      if (statusFilter) p.append('success', statusFilter);
      if (search)       p.append('search', search);

      const res  = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADMIN_QUERY_RECORDS}?${p}`);
      const data = await res.json();
      if (data.success !== false) {
        setPostgresEnabled(data.postgres_enabled !== false);
        const d = data.data || {};
        setQueries(d.records || []);
        const pg = d.page || 1, tp = d.total_pages || 1;
        setPagination({ page: pg, per_page: d.per_page || perPage,
          total: d.total || 0, total_pages: tp, has_prev: pg > 1, has_next: pg < tp });
      } else setError(data.error || 'Failed to load');
    } catch (e) {
      setError('Cannot reach API server. Is it running?');
      console.error(e);
    } finally { setLoading(false); }
  }, [page, perPage, days, domain, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };
  const clearAll = () => { setSearch(''); setSearchInput(''); setDays(30); setStatusFilter(''); setDomain(''); setPage(1); };
  const toggle   = (i) => setExpanded(expanded === i ? null : i);
  const hasFilters = search || domain || statusFilter || days !== 30;

  // Submit corrected SQL to feedback cache
  const submitCorrection = async (query) => {
    if (!correctionSql.trim()) return;
    setCorrectionStatus('saving');
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBMIT_FEEDBACK}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: query.session_id || 'admin_correction',
          query: query.question,
          generated_sql: query.generated_sql || '',
          corrected_sql: correctionSql.trim(),
          company_id: query.company_id || 'all',
          was_correct: false,
          created_by: 'admin',
          skip_safety: true,
          tables_used: query.tables_used || [],
        }),
      });
      if (res.ok) {
        setCorrectionStatus('saved');
        setCorrectedQueries(prev => ({ ...prev, [query.id]: correctionSql.trim() }));
        setApproveStatus(prev => ({ ...prev, [query.id]: 'saved' }));
        setTimeout(() => { setCorrectionId(null); setCorrectionSql(''); setCorrectionStatus(null); }, 2000);
      } else {
        setCorrectionStatus('error');
      }
    } catch (e) {
      console.error('Correction submit failed:', e);
      setCorrectionStatus('error');
    }
  };

  // Approve (thumbs up) — store the generated SQL as validated
  const approveQuery = async (query) => {
    setApproveStatus(prev => ({ ...prev, [query.id]: 'saving' }));
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBMIT_FEEDBACK}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: query.session_id || 'admin_approval',
          query: query.question,
          generated_sql: query.generated_sql || '',
          company_id: query.company_id || 'all',
          was_correct: true,
          created_by: 'admin',
          skip_safety: true,
          tables_used: query.tables_used || [],
        }),
      });
      if (res.ok) {
        setApproveStatus(prev => ({ ...prev, [query.id]: 'saved' }));
      } else {
        setApproveStatus(prev => ({ ...prev, [query.id]: 'error' }));
      }
    } catch (e) {
      console.error('Approve failed:', e);
      setApproveStatus(prev => ({ ...prev, [query.id]: 'error' }));
    }
  };

  /* ── Loading skeleton ─────────────────────────────────────────────── */
  if (loading && queries.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: t.page, display: 'flex',
        alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: `4px solid ${t.border}`,
            borderTopColor: '#3b82f6', borderRadius: '50%', margin: '0 auto 18px',
            animation: 'spin 0.75s linear infinite' }} />
          <p style={{ color: t.textMuted, fontSize: 16, margin: 0 }}>Loading query history…</p>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: embedded ? 'auto' : '100vh', background: t.page, color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      transition: 'background 0.2s, color 0.2s' }}>

      {/* ══ TOP NAV BAR (hidden when embedded in AdminShell) ═══════════ */}
      {!embedded && (
      <div style={{ background: t.surface, borderBottom: `1px solid ${t.border}`,
        padding: '0 32px', position: 'sticky', top: 0, zIndex: 20,
        backdropFilter: 'blur(12px)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

            {/* Left: back + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 7,
                padding: '7px 16px', background: 'transparent', border: `1px solid ${t.border}`,
                borderRadius: 8, cursor: 'pointer', color: t.textSecondary, fontSize: 14, fontWeight: 500 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
                Back
              </button>

              <div style={{ width: 1, height: 28, background: t.border }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#2563eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                </div>
                <div>
                  <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: t.text }}>
                    Query History
                  </h1>
                  <p style={{ margin: 0, fontSize: 12, color: t.textMuted }}>
                    Persistent log · PostgreSQL
                    {!postgresEnabled &&
                      <span style={{ marginLeft: 8, color: '#f59e0b', fontWeight: 600 }}>⚠ PG disabled</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: refresh + theme toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 7,
                padding: '7px 16px', background: dark ? '#1d4ed820' : '#eff6ff',
                border: `1px solid ${dark ? '#1d4ed840' : '#bfdbfe'}`,
                borderRadius: 8, cursor: 'pointer', color: '#3b82f6', fontSize: 14, fontWeight: 500 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                Refresh
              </button>

            {/* Theme toggle — only show when standalone (not embedded in AdminShell) */}
            {darkProp === undefined && (
            <button onClick={() => setDarkLocal(d => !d)}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: t.surfaceDeep, border: `1px solid ${t.border}`,
                borderRadius: 10, cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>
              {dark ? '☀️' : '🌙'}
            </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Embedded: compact refresh */}
      {embedded && (
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 32px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 7,
            padding: '7px 16px', background: dark ? '#1d4ed820' : '#eff6ff',
            border: `1px solid ${dark ? '#1d4ed840' : '#bfdbfe'}`,
            borderRadius: 8, cursor: 'pointer', color: '#3b82f6', fontSize: 14, fontWeight: 500 }}>
            Refresh
          </button>
        </div>
      )}

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 32px' }}>

        {/* ══ FILTER BAR ═══════════════════════════════════════════════════ */}
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: '16px 18px', marginBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>

            {/* Search */}
            <form onSubmit={onSearch} style={{ display: 'flex', gap: 8, flex: '1 1 280px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2"
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search by question…"
                  style={{ width: '100%', padding: '10px 14px 10px 36px', background: t.inputBg,
                    border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 15,
                    outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <button type="submit" style={{ padding: '10px 20px', background: '#2563eb',
                border: 'none', borderRadius: 8, color: 'white', cursor: 'pointer',
                fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap' }}>
                Search
              </button>
            </form>

            {/* Dropdowns */}
            {[
              { val: days, set: (v) => { setDays(Number(v)); setPage(1); }, opts: [
                [7,'Last 7 days'],[14,'Last 14 days'],[30,'Last 30 days'],[60,'Last 60 days'],[90,'Last 90 days']
              ]},
              { val: statusFilter, set: (v) => { setStatusFilter(v); setPage(1); }, opts: [
                ['','All results'],['true','✓  Successful only'],['false','✗  Failed only']
              ]},
            ].map((s, i) => (
              <select key={i} value={s.val} onChange={e => s.set(e.target.value)}
                style={{ padding: '10px 12px', background: t.inputBg, border: `1px solid ${t.border}`,
                  borderRadius: 8, color: t.text, fontSize: 15, cursor: 'pointer' }}>
                {s.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ))}

            <input type="text" value={domain} onChange={e => { setDomain(e.target.value); setPage(1); }}
              placeholder="Domain…"
              style={{ padding: '10px 14px', background: t.inputBg, border: `1px solid ${t.border}`,
                borderRadius: 8, color: t.text, fontSize: 15, width: 140 }} />

            {hasFilters && (
              <button onClick={clearAll}
                style={{ padding: '10px 16px', background: 'transparent', border: `1px solid ${t.border}`,
                  borderRadius: 8, color: t.textMuted, cursor: 'pointer', fontSize: 15, fontWeight: 500 }}>
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        {/* ══ ERROR ════════════════════════════════════════════════════════ */}
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: t.errorBg, border: `1px solid ${t.errorBorder}`, borderRadius: 10,
            padding: '14px 20px', marginBottom: 20, color: t.errorText, fontSize: 15 }}>
            <span>⚠ {error}</span>
            <button onClick={load} style={{ padding: '5px 16px', background: t.errorBorder,
              border: 'none', borderRadius: 7, color: t.errorText, cursor: 'pointer', fontSize: 14 }}>
              Retry
            </button>
          </div>
        )}

        {/* ══ TABLE ════════════════════════════════════════════════════════ */}
        <div style={{ background: t.surface, border: `1px solid ${t.border}`,
          borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>

              <thead>
                <tr style={{ background: t.headBg, borderBottom: `1px solid ${t.border}` }}>
                  {['Timestamp', 'Question', 'Domain', 'Status', 'Rows', 'Gen Time', 'Session', ''].map(h => (
                    <th key={h} style={{ padding: '13px 18px', textAlign: 'left', fontSize: 12,
                      fontWeight: 700, color: t.textMuted, textTransform: 'uppercase',
                      letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {queries.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '72px 0', textAlign: 'center' }}>
                      <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={t.border}
                        strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 16px' }}>
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <p style={{ color: t.textMuted, fontSize: 16, margin: 0, fontWeight: 500 }}>
                        No queries match your filters
                      </p>
                    </td>
                  </tr>
                ) : queries.map((q, i) => {
                  const ds   = getDomain(q.domain, dark);
                  const open = expanded === i;

                  return (
                    <React.Fragment key={q.id ?? i}>
                      {/* ── Row ── */}
                      <tr onClick={() => toggle(i)}
                        style={{ cursor: 'pointer', borderBottom: `1px solid ${t.borderSoft}`,
                          background: open ? t.rowHover : 'transparent', transition: 'background 0.1s' }}
                        onMouseEnter={e => { if (!open) e.currentTarget.style.background = t.rowHover; }}
                        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}>

                        {/* Timestamp */}
                        <td style={{ padding: '14px 18px', whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 14, color: t.textSecondary }}>
                            {fmtDate(q.ts || q.timestamp)}
                          </span>
                        </td>

                        {/* Question */}
                        <td style={{ padding: '14px 18px', maxWidth: 400 }}>
                          <span style={{ fontSize: 15, color: t.text, lineHeight: 1.5 }}>
                            {trunc(q.question)}
                          </span>
                        </td>

                        {/* Domain */}
                        <td style={{ padding: '14px 18px', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                            padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                            background: ds.bg, color: ds.text }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%',
                              background: ds.dot, flexShrink: 0 }} />
                            {q.domain || 'unknown'}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '14px 18px', whiteSpace: 'nowrap' }}>
                          {q.execution_success
                            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                                background: dark ? '#06652620' : '#d1fae5', color: dark ? '#34d399' : '#065f46' }}>
                                <span style={{ fontSize: 9 }}>●</span> Success
                              </span>
                            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                                background: dark ? '#dc262620' : '#fee2e2', color: dark ? '#f87171' : '#991b1b' }}>
                                <span style={{ fontSize: 9 }}>●</span> Failed
                              </span>
                          }
                        </td>

                        {/* Rows */}
                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                          <span style={{ fontSize: 15, color: t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                            {q.row_count ?? '—'}
                          </span>
                        </td>

                        {/* Gen time */}
                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                          <span style={{ fontSize: 15, color: t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                            {msLabel(q.generation_time_ms)}
                          </span>
                        </td>

                        {/* Session */}
                        <td style={{ padding: '14px 18px' }}>
                          <span title={q.session_id} style={{ fontSize: 13, color: t.textFaint,
                            fontFamily: 'ui-monospace, monospace' }}>
                            {q.session_id ? q.session_id.slice(0, 13) + '…' : '—'}
                          </span>
                        </td>

                        {/* Chevron */}
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke={t.textMuted} strokeWidth="2.5"
                            style={{ display: 'block', margin: '0 auto',
                              transform: open ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s' }}>
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        </td>
                      </tr>

                      {/* ── Expanded panel ── */}
                      {open && (
                        <tr style={{ background: t.expandedBg }}>
                          <td colSpan={8} style={{ padding: 0 }}>
                            <div style={{ borderLeft: '3px solid #2563eb',
                              margin: '0 20px 20px 20px', paddingLeft: 22, paddingTop: 22 }}>

                              {/* Question + SQL */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr',
                                gap: 18, marginBottom: 22 }}>
                                <div>
                                  <SectionLabel t={t}>Full Question</SectionLabel>
                                  <div style={{ background: t.surface, border: `1px solid ${t.border}`,
                                    borderRadius: 10, padding: '14px 18px', fontSize: 15,
                                    color: t.text, lineHeight: 1.7 }}>
                                    {q.question || '—'}
                                  </div>
                                </div>
                                <div>
                                  <SectionLabel t={t}>Generated SQL</SectionLabel>
                                  <pre style={{ margin: 0, background: t.codeBg,
                                    border: `1px solid ${dark ? '#1e3a5f' : t.border}`,
                                    borderRadius: 10, padding: '14px 18px', color: t.codeText,
                                    fontSize: 14, fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                                    overflowX: 'auto', maxHeight: 240, lineHeight: 1.7,
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {q.generated_sql || 'No SQL generated'}
                                  </pre>
                                </div>
                              </div>

                              {/* ── Approve / Correct actions ── */}
                              <div style={{ marginBottom: 20, padding: '12px 18px',
                                background: t.surface, border: `1px solid ${t.border}`,
                                borderRadius: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                                  marginBottom: correctionId === q.id ? 14 : 0 }}>
                                  <button onClick={() => approveQuery(q)}
                                    disabled={approveStatus[q.id] === 'saving' || approveStatus[q.id] === 'saved' || !!correctedQueries[q.id] || correctionStatus === 'saved'}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6,
                                      padding: '8px 16px', borderRadius: 8, border: 'none',
                                      cursor: (approveStatus[q.id] === 'saved' || correctedQueries[q.id]) ? 'default' : 'pointer',
                                      fontSize: 13, fontWeight: 600,
                                      background: (approveStatus[q.id] === 'saved' || correctedQueries[q.id]) ? '#052e16' : '#166534',
                                      color: '#4ade80',
                                      opacity: (approveStatus[q.id] === 'saving') ? 0.6 : (approveStatus[q.id] === 'saved' || correctedQueries[q.id]) ? 0.7 : 1 }}>
                                    {(approveStatus[q.id] === 'saved' || correctedQueries[q.id]) ? 'Approved' :
                                     approveStatus[q.id] === 'saving' ? 'Saving...' : 'Approve Query'}
                                  </button>
                                  <button onClick={() => {
                                      if (correctedQueries[q.id]) return;
                                      if (correctionId === q.id) {
                                        setCorrectionId(null); setCorrectionSql(''); setCorrectionStatus(null);
                                      } else {
                                        setCorrectionId(q.id); setCorrectionSql(q.generated_sql || ''); setCorrectionStatus(null);
                                      }
                                    }}
                                    disabled={!!correctedQueries[q.id]}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6,
                                      padding: '8px 16px', borderRadius: 8, border: 'none',
                                      cursor: correctedQueries[q.id] ? 'default' : 'pointer',
                                      fontSize: 13, fontWeight: 600,
                                      background: correctedQueries[q.id] ? '#052e16' : correctionId === q.id ? '#7f1d1d' : '#450a0a',
                                      color: correctedQueries[q.id] ? '#4ade80' : '#fca5a5',
                                      opacity: correctedQueries[q.id] ? 0.7 : 1 }}>
                                    {correctedQueries[q.id] ? 'Corrected' : correctionId === q.id ? 'Cancel' : 'Correct Query'}
                                  </button>
                                  {approveStatus[q.id] === 'error' && (
                                    <span style={{ fontSize: 12, color: '#ef4444' }}>Failed to save</span>
                                  )}
                                </div>
                                {correctionId === q.id && (
                                  <div>
                                    <p style={{ margin: '0 0 8px', fontSize: 12, color: t.textMuted }}>
                                      Paste the corrected SQL below:
                                    </p>
                                    <textarea value={correctionSql}
                                      onChange={e => setCorrectionSql(e.target.value)} rows={6}
                                      style={{ width: '100%', padding: '12px 14px', background: t.codeBg,
                                        border: `1px solid ${dark ? '#1e3a5f' : t.border}`,
                                        borderRadius: 8, color: t.codeText, fontSize: 13,
                                        fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                                        lineHeight: 1.6, resize: 'vertical', outline: 'none',
                                        boxSizing: 'border-box' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                                      <button onClick={() => submitCorrection(q)}
                                        disabled={correctionStatus === 'saving' || !correctionSql.trim()}
                                        style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
                                          background: '#2563eb', color: '#fff', cursor: 'pointer',
                                          fontSize: 13, fontWeight: 600,
                                          opacity: (correctionStatus === 'saving' || !correctionSql.trim()) ? 0.5 : 1 }}>
                                        {correctionStatus === 'saving' ? 'Saving...' : 'Submit Correction'}
                                      </button>
                                      {correctionStatus === 'saved' && (
                                        <span style={{ fontSize: 13, color: '#4ade80', fontWeight: 600 }}>Saved to feedback cache</span>
                                      )}
                                      {correctionStatus === 'error' && (
                                        <span style={{ fontSize: 13, color: '#ef4444' }}>Failed to save</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {correctedQueries[q.id] && correctionId !== q.id && (
                                  <div style={{ marginTop: 12 }}>
                                    <p style={{ margin: '0 0 6px', fontSize: 12, color: '#4ade80', fontWeight: 600 }}>
                                      Corrected SQL (saved to feedback cache):
                                    </p>
                                    <pre style={{ margin: 0, padding: '12px 14px', background: t.codeBg,
                                      border: `1px solid ${dark ? '#064e3b' : '#065f46'}`,
                                      borderRadius: 8, color: t.codeText, fontSize: 13,
                                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                                      lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {correctedQueries[q.id]}
                                    </pre>
                                  </div>
                                )}
                              </div>

                              {/* Performance */}
                              <div style={{ marginBottom: 20 }}>
                                <SectionLabel t={t}>Performance</SectionLabel>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                  <MetaTile t={t} label="Exec Time"      value={msLabel(q.execution_time_ms)}  accent="#60a5fa" />
                                  <MetaTile t={t} label="Gen Time"       value={msLabel(q.generation_time_ms)} accent="#a78bfa" />
                                  <MetaTile t={t} label="Rows Returned"  value={q.row_count ?? '—'}            accent="#10b981" />
                                  <MetaTile t={t} label="Context Tokens" value={q.context_tokens ?? '—'}       accent="#f59e0b" />
                                  <MetaTile t={t} label="Corrections"    value={q.correction_attempts ?? 0}    accent="#f97316" />
                                  <MetaTile t={t} label="Model"          value={q.model_used || 'default'}     accent={t.textSecondary} />
                                </div>
                              </div>

                              {/* Flags */}
                              <div style={{ marginBottom: 20 }}>
                                <SectionLabel t={t}>Processing Flags</SectionLabel>
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                  <FlagPill t={t} on={!!q.validation_passed}    label="Validation passed" />
                                  <FlagPill t={t} on={!!q.used_cache}           label="Cache hit" />
                                  <FlagPill t={t} on={!!q.used_two_stage}       label="Two-stage generation" />
                                  <FlagPill t={t} on={!!q.used_self_correction} label="Self-correction" />
                                  <FlagPill t={t} on={!!q.used_fallback}        label="Fallback used" />
                                </div>
                              </div>

                              {/* Corrections chips */}
                              {((q.columns_corrected?.length > 0) || (q.joins_modified?.length > 0)) && (
                                <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 20 }}>
                                  {q.columns_corrected?.length > 0 && (
                                    <div>
                                      <SectionLabel t={t}>Columns Auto-Corrected</SectionLabel>
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {q.columns_corrected.map((c, j) => <TagChip key={j} label={c} color="#f59e0b" />)}
                                      </div>
                                    </div>
                                  )}
                                  {q.joins_modified?.length > 0 && (
                                    <div>
                                      <SectionLabel t={t}>Joins Modified</SectionLabel>
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {q.joins_modified.map((j, k) => <TagChip key={k} label={j} color="#3b82f6" />)}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Tables used */}
                              {q.tables_used?.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                  <SectionLabel t={t}>Tables Used</SectionLabel>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {q.tables_used.map((t2, j) => <TagChip key={j} label={t2} color={t.textSecondary} />)}
                                  </div>
                                </div>
                              )}

                              {/* Errors */}
                              {(q.schema_errors?.length > 0 || q.execution_error) && (
                                <div style={{ background: t.errorBg, border: `1px solid ${t.errorBorder}`,
                                  borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
                                  <SectionLabel t={{ textMuted: t.errorText }}>Errors</SectionLabel>
                                  {q.execution_error &&
                                    <p style={{ margin: '0 0 6px', color: t.errorText, fontSize: 15 }}>
                                      {q.execution_error}
                                    </p>}
                                  {q.schema_errors?.map((e, j) =>
                                    <p key={j} style={{ margin: '4px 0', color: t.errorText, fontSize: 15 }}>
                                      • {e}
                                    </p>)}
                                </div>
                              )}

                              {/* Footer: Session + Time */}
                              <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap',
                                paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
                                <div>
                                  <p style={{ margin: '0 0 4px', fontSize: 11, color: t.textMuted,
                                    textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                                    Session ID
                                  </p>
                                  <code style={{ fontSize: 14, color: t.textSecondary,
                                    fontFamily: 'ui-monospace, monospace' }}>
                                    {q.session_id || '—'}
                                  </code>
                                </div>
                                <div>
                                  <p style={{ margin: '0 0 4px', fontSize: 11, color: t.textMuted,
                                    textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                                    Timestamp
                                  </p>
                                  <code style={{ fontSize: 14, color: t.textSecondary,
                                    fontFamily: 'ui-monospace, monospace' }}>
                                    {fmtDate(q.ts || q.timestamp)}
                                  </code>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ══ PAGINATION ══════════════════════════════════════════════════ */}
          {pagination.total_pages > 1 && (
            <div style={{ background: t.paginationBg, borderTop: `1px solid ${t.border}`,
              padding: '14px 22px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>

              <p style={{ margin: 0, fontSize: 15, color: t.textMuted }}>
                Showing{' '}
                <strong style={{ color: t.text }}>
                  {((pagination.page - 1) * pagination.per_page) + 1}–
                  {Math.min(pagination.page * pagination.per_page, pagination.total)}
                </strong>
                {' '}of{' '}
                <strong style={{ color: t.text }}>{pagination.total}</strong> queries
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {[
                  { label: '«',      action: () => setPage(1),                disabled: !pagination.has_prev },
                  { label: '‹ Prev', action: () => setPage(p => p - 1),      disabled: !pagination.has_prev },
                ].map(b => (
                  <button key={b.label} onClick={b.action} disabled={b.disabled}
                    style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${t.border}`,
                      background: 'transparent', color: b.disabled ? t.border : t.textSecondary,
                      cursor: b.disabled ? 'default' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                    {b.label}
                  </button>
                ))}

                <span style={{ padding: '7px 18px', background: dark ? '#1d4ed820' : '#eff6ff',
                  border: `1px solid ${dark ? '#1d4ed840' : '#bfdbfe'}`,
                  borderRadius: 8, fontSize: 14, color: dark ? '#60a5fa' : '#1d4ed8',
                  fontWeight: 700, minWidth: 90, textAlign: 'center' }}>
                  {pagination.page} / {pagination.total_pages}
                </span>

                {[
                  { label: 'Next ›', action: () => setPage(p => p + 1),      disabled: !pagination.has_next },
                  { label: '»',      action: () => setPage(pagination.total_pages), disabled: !pagination.has_next },
                ].map(b => (
                  <button key={b.label} onClick={b.action} disabled={b.disabled}
                    style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${t.border}`,
                      background: 'transparent', color: b.disabled ? t.border : t.textSecondary,
                      cursor: b.disabled ? 'default' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══ LOADING OVERLAY ══════════════════════════════════════════════ */}
      {loading && queries.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: t.surface, border: `1px solid ${t.border}`,
            borderRadius: 14, padding: '30px 44px', textAlign: 'center' }}>
            <div style={{ width: 40, height: 40, border: `4px solid ${t.border}`,
              borderTopColor: '#3b82f6', borderRadius: '50%', margin: '0 auto 16px',
              animation: 'spin 0.75s linear infinite' }} />
            <p style={{ color: t.textMuted, fontSize: 15, margin: 0, fontWeight: 500 }}>Refreshing data…</p>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default QueryHistory;
